/**
 * Custom JavaScript for modular sections.
 * This file contains reusable scripts for custom sections.
 */

document.addEventListener('DOMContentLoaded', () => {
  initCustomVideos();
  initColumnCarousels();
});

document.addEventListener('shopify:section:load', () => {
  initCustomVideos();
  initColumnCarousels();
});

document.addEventListener('shopify:section:unload', (event) => {
  const root = event.target.querySelector('[data-column-carousel]');
  if (root && root._columnCarousel) {
    root._columnCarousel.destroy();
    root._columnCarousel = null;
  }
});

function initCustomVideos() {
  const videoWrappers = document.querySelectorAll('.custom-video-wrapper[data-has-video="true"]');

  videoWrappers.forEach((wrapper) => {
    if (wrapper.dataset.videoInitialized) return;
    wrapper.dataset.videoInitialized = 'true';

    const video = wrapper.querySelector('video');
    if (!video) return;

    const togglePlayback = () => {
      if (video.paused) {
        if (typeof window.pauseAllMedia === 'function') {
          window.pauseAllMedia();
        }

        document.querySelectorAll('.custom-video-wrapper video').forEach((otherVideo) => {
          if (otherVideo !== video && !otherVideo.paused) {
            otherVideo.pause();
          }
        });

        video.play().catch(function () {});
      } else {
        video.pause();
      }
    };

    wrapper.addEventListener('click', togglePlayback);

    wrapper.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        togglePlayback();
      }
    });

    video.addEventListener('play', function () {
      wrapper.classList.add('is-playing', 'is-started');
      wrapper.setAttribute('aria-label', 'Pause video');
    });

    video.addEventListener('pause', function () {
      wrapper.classList.remove('is-playing');
      wrapper.setAttribute('aria-label', 'Play video');
    });

    video.addEventListener('ended', function () {
      wrapper.classList.remove('is-playing');
      wrapper.setAttribute('aria-label', 'Play video');
    });
  });
}

/**
 * Reusable multi-column carousel
 * Markup contract:
 *   [data-column-carousel]
 *     [data-column-carousel-track]   — scroll container with item children
 *     [data-column-carousel-controls]— optional footer (progress + nav)
 *       [data-column-carousel-progress-bar]
 *       [data-column-carousel-progress]
 *       [data-column-carousel-prev]
 *       [data-column-carousel-next]
 *
 * data-columns-desktop / data-columns-tablet / data-columns-mobile
 */
function initColumnCarousels() {
  document.querySelectorAll('[data-column-carousel]').forEach((root) => {
    if (root._columnCarousel) {
      root._columnCarousel.destroy();
      root._columnCarousel = null;
    }
    root._columnCarousel = new ColumnCarousel(root);
  });
}

class ColumnCarousel {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-column-carousel-track]');
    this.controls = root.querySelector('[data-column-carousel-controls]');
    this.progressBar = root.querySelector('[data-column-carousel-progress-bar]');
    this.progressFill = root.querySelector('[data-column-carousel-progress]');
    this.btnPrev = root.querySelector('[data-column-carousel-prev]');
    this.btnNext = root.querySelector('[data-column-carousel-next]');

    this.columnsDesktop = parseInt(root.dataset.columnsDesktop, 10) || 3;
    this.columnsTablet = parseInt(root.dataset.columnsTablet, 10) || 2;
    this.columnsMobile = parseInt(root.dataset.columnsMobile, 10) || 1;

    this.current = 0;
    this.rafId = null;

    if (!this.track) return;

    this.items = Array.from(this.track.children).filter((el) => el.nodeType === 1);
    this.total = this.items.length;

    this.onPrev = () => this.goTo(this.current - 1);
    this.onNext = () => this.goTo(this.current + 1);
    this.onScroll = () => this.scheduleSync();
    this.onResize = () => {
      this.applyColumnVars();
      this.updateControlsVisibility();
      this.scheduleSync();
    };

    this.applyColumnVars();
    this.updateControlsVisibility();
    this.bindEvents();
    this.syncFromScroll();
  }

  bindEvents() {
    if (this.btnPrev) this.btnPrev.addEventListener('click', this.onPrev);
    if (this.btnNext) this.btnNext.addEventListener('click', this.onNext);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);
  }

  destroy() {
    if (this.btnPrev) this.btnPrev.removeEventListener('click', this.onPrev);
    if (this.btnNext) this.btnNext.removeEventListener('click', this.onNext);
    if (this.track) this.track.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    if (this.rafId) cancelAnimationFrame(this.rafId);
  }

  getVisibleColumns() {
    const width = window.innerWidth;
    if (width >= 990) return this.columnsDesktop;
    if (width >= 750) return this.columnsTablet;
    return this.columnsMobile;
  }

  getMaxIndex() {
    return Math.max(0, this.total - this.getVisibleColumns());
  }

  applyColumnVars() {
    this.root.style.setProperty('--column-carousel-cols-desktop', String(this.columnsDesktop));
    this.root.style.setProperty('--column-carousel-cols-tablet', String(this.columnsTablet));
    this.root.style.setProperty('--column-carousel-cols-mobile', String(this.columnsMobile));
    this.root.style.setProperty('--column-carousel-cols', String(this.getVisibleColumns()));
  }

  updateControlsVisibility() {
    if (!this.controls) return;
    const show = this.total > this.getVisibleColumns();
    this.controls.hidden = !show;
    this.root.classList.toggle('column-carousel--active', show);
  }

  getItemStride() {
    if (!this.items.length) return 0;
    const first = this.items[0];
    const style = window.getComputedStyle(this.track);
    const gap = parseFloat(style.columnGap || style.gap) || 0;
    return first.getBoundingClientRect().width + gap;
  }

  scheduleSync() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.syncFromScroll();
    });
  }

  syncFromScroll() {
    const stride = this.getItemStride();
    if (stride <= 0) {
      this.syncMeta(0);
      return;
    }

    const index = Math.round(this.track.scrollLeft / stride);
    this.current = Math.max(0, Math.min(this.getMaxIndex(), index));
    this.syncMeta(this.current);
  }

  syncMeta(index) {
    const maxIndex = this.getMaxIndex();
    const steps = maxIndex + 1;
    const page = Math.max(0, Math.min(maxIndex, index));

    if (this.progressFill) {
      this.progressFill.style.width = `${((page + 1) / steps) * 100}%`;
    }

    if (this.progressBar) {
      this.progressBar.setAttribute('aria-valuemax', String(steps));
      this.progressBar.setAttribute('aria-valuenow', String(page + 1));
    }

    if (this.btnPrev) {
      const disabled = page <= 0;
      this.btnPrev.disabled = disabled;
      this.btnPrev.classList.toggle('is-disabled', disabled);
    }

    if (this.btnNext) {
      const disabled = page >= maxIndex;
      this.btnNext.disabled = disabled;
      this.btnNext.classList.toggle('is-disabled', disabled);
    }
  }

  goTo(index) {
    const maxIndex = this.getMaxIndex();
    const next = Math.max(0, Math.min(maxIndex, index));
    const stride = this.getItemStride();
    if (stride <= 0) return;

    this.current = next;
    this.track.scrollTo({
      left: next * stride,
      behavior: 'smooth',
    });
    this.syncMeta(next);
  }
}

window.ColumnCarousel = ColumnCarousel;
window.initColumnCarousels = initColumnCarousels;
